import { Link, useLocation } from "react-router-dom";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount, useBalance } from "wagmi";
import { useFactoryOwner } from "@/hooks/useMarkets";
import { useUSDTBalance } from "@/hooks/useUSDT";
import { formatUSDT } from "@/lib/contracts";
import EtherMarketLogo from "@/components/brand/EtherMarketLogo";

const navigation = [
  { name: "Markets", href: "/" },
  { name: "Create", href: "/create" },
  { name: "Portfolio", href: "/my-predictions" },
];

export default function Header() {
  const location = useLocation();
  const { address, isConnected } = useAccount();
  const { data: ownerAddress } = useFactoryOwner();

  const isOwner = isConnected && address && ownerAddress && address.toLowerCase() === ownerAddress.toLowerCase();

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center">
              <EtherMarketLogo />
            </Link>
            <span className="badge badge-yellow" title="Running on a test network — not live funds">
              Testnet
            </span>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
            {isOwner && (
              <Link
                to="/admin"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  location.pathname === "/admin"
                    ? "bg-yellow-50 text-yellow-700"
                    : "text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Admin
              </Link>
            )}
          </nav>

          {/* Wallet Connection */}
          <div className="flex items-center space-x-4">
            {isConnected && (
              <div className="hidden sm:flex items-center gap-3 text-sm text-gray-500">
                <EthBalance />
                <USDTBalance />
              </div>
            )}
            <ConnectButton
              showBalance={false}
              chainStatus="icon"
              accountStatus={{
                smallScreen: "avatar",
                largeScreen: "full",
              }}
            />
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden pb-3 flex space-x-2 overflow-x-auto">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                  isActive
                    ? "bg-primary-50 text-primary-700"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {item.name}
              </Link>
            );
          })}
          {isOwner && (
            <Link
              to="/admin"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap ${
                location.pathname === "/admin"
                  ? "bg-yellow-50 text-yellow-700"
                  : "text-yellow-600 hover:bg-yellow-50"
              }`}
            >
              Admin
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function USDTBalance() {
  const { address } = useAccount();
  const { data: balance } = useUSDTBalance(address);

  // Only hide while the balance is still loading; show 0.00 for a zero balance
  // (0n is falsy, so `if (!balance)` previously hid wallets holding no USDT).
  if (balance === undefined) return null;

  return (
    <span className="font-medium">
      {formatUSDT(balance)} USDT
    </span>
  );
}

function EthBalance() {
  const { address } = useAccount();
  const { data } = useBalance({ address });

  // Native gas-token balance (ETH). Only hide while loading; show 0 otherwise.
  if (!data) return null;

  return (
    <span className="font-medium">
      {Number(data.formatted).toFixed(4)} {data.symbol}
    </span>
  );
}
